import React, { useCallback, useEffect, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { getRouteTab } from "@core/route-tab-meta";
import NavMenu from "./nav-menu";
import TabMenu from "./tab-menu";
import DialogUnreadListener from "./dialog-unread-listener";
import { tabPathname } from "./tab-url";
import "./app-shell.css";

const LS_TABS_KEY = "planOnline.tabMenu.tabs";

const loadTabsFromStorage = () => {
  try {
    const raw = localStorage.getItem(LS_TABS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    const seen = new Set();
    return parsed
      .map((t) => {
        const rawUrl = typeof t.url === "string" ? t.url : "";
        const path = tabPathname(rawUrl);
        const search = rawUrl.includes("?") ? rawUrl.slice(rawUrl.indexOf("?")) : "";
        const normalizedPath = path === "/numbers" ? "/hub/numbers" : path;
        const routeTab = getRouteTab(normalizedPath);
        const url = `${routeTab?.url || normalizedPath}${search}`;
        return {
          title: routeTab?.title || (typeof t.title === "string" ? t.title : ""),
          url
        };
      })
      .filter((t) => {
        const path = tabPathname(t.url);
        if (!t.title || !path || seen.has(path)) return false;
        seen.add(path);
        return true;
      });
  } catch {
    return [];
  }
};

const AppShell = ({ onLogout }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const pathname = location.pathname;
  const search = location.search || "";

  const [tabs, setTabs] = useState(loadTabsFromStorage);

  useEffect(() => {
    localStorage.setItem(LS_TABS_KEY, JSON.stringify(tabs));
  }, [tabs]);

  useEffect(() => {
    const routeTab = getRouteTab(pathname);
    if (!routeTab) return;
    const baseUrl = routeTab.url;
    const isListPage = pathname === baseUrl;
    setTabs((prev) => {
      const idx = prev.findIndex((t) => tabPathname(t.url) === baseUrl);
      if (idx >= 0) {
        const existing = prev[idx];
        // На списке — запоминаем query во вкладке; на карточке — не затираем.
        const url = isListPage ? `${baseUrl}${search}` : existing.url;
        if (existing.title === routeTab.title && existing.url === url) return prev;
        return prev.map((t, i) =>
          i === idx ? { ...t, title: routeTab.title, url } : t
        );
      }
      return [...prev, { title: routeTab.title, url: isListPage ? `${baseUrl}${search}` : baseUrl }];
    });
  }, [pathname, search]);

  const onOpenTab = useCallback((tab) => {
    const base = tabPathname(tab.url);
    setTabs((prev) => {
      const existing = prev.find((t) => tabPathname(t.url) === base);
      if (existing) {
        if (existing.title === tab.title) return prev;
        return prev.map((t) =>
          tabPathname(t.url) === base ? { ...t, title: tab.title } : t
        );
      }
      return [...prev, tab];
    });
  }, []);

  const onCloseTab = useCallback(
    (url) => {
      const base = tabPathname(url);
      setTabs((prev) => {
        const idx = prev.findIndex((t) => tabPathname(t.url) === base);
        const next = prev.filter((t) => tabPathname(t.url) !== base);
        if (tabPathname(pathname) === base || pathname.startsWith(`${base}/`)) {
          const target = next[idx - 1]?.url ?? next[idx]?.url ?? "/";
          navigate(target);
        }
        return next;
      });
    },
    [navigate, pathname]
  );

  const onCloseOtherTabs = useCallback(
    (keepUrl) => {
      const keepBase = tabPathname(keepUrl);
      setTabs((prev) => {
        const keep = prev.find((t) => tabPathname(t.url) === keepBase);
        if (!keep || prev.length <= 1) return prev;
        return [keep];
      });
      navigate(keepUrl);
    },
    [navigate]
  );

  const onCloseTabsToTheRight = useCallback(
    (fromUrl) => {
      const fromBase = tabPathname(fromUrl);
      setTabs((prev) => {
        const idx = prev.findIndex((t) => tabPathname(t.url) === fromBase);
        if (idx < 0) return prev;
        const toRemove = prev.slice(idx + 1);
        const next = prev.slice(0, idx + 1);
        const currentBase = tabPathname(pathname);
        if (
          toRemove.some(
            (t) =>
              tabPathname(t.url) === currentBase ||
              pathname.startsWith(`${tabPathname(t.url)}/`)
          )
        ) {
          navigate(fromUrl);
        }
        return next;
      });
    },
    [navigate, pathname]
  );

  const onCloseAllTabs = useCallback(() => {
    const homeTab = getRouteTab("/");
    setTabs(homeTab ? [homeTab] : []);
    navigate("/");
  }, [navigate]);

  return (
    <div className="app-shell">
      <NavMenu onOpenTab={onOpenTab} onLogout={onLogout} />
      <div className="app-shell__main">
        <TabMenu
          tabs={tabs}
          onOpenTab={onOpenTab}
          onCloseTab={onCloseTab}
          onCloseOtherTabs={onCloseOtherTabs}
          onCloseTabsToTheRight={onCloseTabsToTheRight}
          onCloseAllTabs={onCloseAllTabs}
        />
        <main className="app-shell__content">
          <DialogUnreadListener />
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default AppShell;
